import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../LanguageContext';
import { brokerCommissions, brokerCommissionReceiptBlobUrl } from '../api';

// Broker-facing list of this company's commissions with their payment status and
// the developer's uploaded receipt (proof the commission was paid). Company-scoped
// + gated by broker.viewFinancials on the server.
export default function BrokerCommissions() {
  const { lang, isRTL } = useLanguage();
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setFailed(false);
    const res = await brokerCommissions();
    if (res.error) { setData(null); setFailed(true); } else { setData(res); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const money = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');
  const viewReceipt = async (dealId, download) => {
    try {
      const url = await brokerCommissionReceiptBlobUrl(dealId);
      if (download) {
        const a = document.createElement('a');
        a.href = url; a.download = `receipt-${dealId}`; document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, '_blank', 'noopener');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { alert(t('تعذّر فتح الإيصال', 'Could not open receipt')); }
  };

  const kpi = (label, value, color) => (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, flex: '1 1 160px', minWidth: 160 }}>
      <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: color || 'var(--text-primary)' }}>{value}</div>
    </div>
  );
  const cell = { padding: '9px 10px', borderBottom: '1px solid var(--border)', fontSize: '0.84rem', textAlign: 'center', whiteSpace: 'nowrap' };
  const statusBadge = (s) => (
    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700,
      background: s === 'received' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
      color: s === 'received' ? '#16a34a' : '#f59e0b' }}>
      {s === 'received' ? t('مستلمة', 'Received') : t('معلّقة', 'Pending')}
    </span>
  );

  const totals = data?.totals || {};
  const rows = data?.commissions || [];

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 1040, margin: '0 auto' }}>
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>...</div>
      ) : failed ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {t('تعذّر تحميل البيانات', 'Could not load data')}
          <div style={{ marginTop: 12 }}>
            <button onClick={load} style={{ padding: '7px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer' }}>{t('إعادة المحاولة', 'Retry')}</button>
          </div>
        </div>
      ) : data?.restricted ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {t('ليس لديك صلاحية لعرض العمولات. تواصل مع مالك الحساب.', 'You do not have permission to view commissions. Contact your account owner.')}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
            {kpi(t('إجمالي العمولات (ر.س)', 'Total commission (SAR)'), money(totals.commission))}
            {kpi(t('مستلمة (ر.س)', 'Received (SAR)'), money(totals.received), 'var(--success, #16a34a)')}
            {kpi(t('معلّقة (ر.س)', 'Pending (SAR)'), money(totals.pending), 'var(--warning, #f59e0b)')}
            {kpi(t('عدد الصفقات', 'Deals'), money(totals.count))}
          </div>

          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr style={{ background: 'var(--bg-primary)' }}>
                  <th style={{ ...cell, textAlign: isRTL ? 'right' : 'left' }}>{t('المشروع', 'Project')}</th>
                  <th style={cell}>{t('الوحدة', 'Unit')}</th>
                  <th style={cell}>{t('النسبة', 'Rate')}</th>
                  <th style={cell}>{t('العمولة (ر.س)', 'Commission')}</th>
                  <th style={cell}>{t('تاريخ الصفقة', 'Deal date')}</th>
                  <th style={cell}>{t('الحالة', 'Status')}</th>
                  <th style={cell}>{t('إثبات الاستلام', 'Proof of receipt')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td style={{ ...cell, textAlign: 'center' }} colSpan={7}>{t('لا توجد عمولات بعد', 'No commissions yet')}</td></tr>
                ) : rows.map((c) => (
                  <tr key={c.dealId}>
                    <td style={{ ...cell, textAlign: isRTL ? 'right' : 'left', fontWeight: 600 }}>{c.project || '—'}</td>
                    <td style={cell}>{c.unit || '—'}</td>
                    <td style={cell}>{c.pct ? `${c.pct}%` : '—'}</td>
                    <td style={{ ...cell, fontWeight: 700 }}>{money(c.amount)}</td>
                    <td style={{ ...cell, color: 'var(--text-secondary)' }}>{fmtDate(c.dealDate)}</td>
                    <td style={cell}>{statusBadge(c.status)}</td>
                    <td style={cell}>
                      {c.hasReceipt ? (
                        <span style={{ display: 'inline-flex', gap: 6, justifyContent: 'center' }}>
                          <button onClick={() => viewReceipt(c.dealId, false)} style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--accent)', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}>{t('عرض', 'View')}</button>
                          <button onClick={() => viewReceipt(c.dealId, true)} style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}>⬇</button>
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{c.status === 'received' ? t('بدون إيصال', 'No receipt') : t('بانتظار الدفع', 'Awaiting payment')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
